import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { CalendarDays, X, Star, ChevronDown } from 'lucide-react';
import { makePeriod, rangeEndExcl, RELATIVE_PRESETS } from '@/lib/dateSlicerUtils';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

const GRAINS = [
  { key: 'range', label: 'Range' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
];

/**
 * Calendar date filter. Emits a structured value the query engine expands:
 *  - { dk:'range', start, endExcl }
 *  - { dk:'periods', grain, periods:[{token,label,start,endExcl}] }
 *  - { dk:'relative', preset }
 *  - null (All)
 */
export function DateSlicerControl({ dimension, label, selected, onChange, fiscalStartMonth = 1 }) {
  const fsm = Number(fiscalStartMonth) || 1;
  const isFiscal = fsm !== 1;
  const sel = (selected && typeof selected === 'object') ? selected : null;
  const initialGrain = sel?.dk === 'periods' ? sel.grain : sel?.dk === 'range' ? 'range' : 'range';
  const [grain, setGrain] = useState(initialGrain);
  const now = new Date();
  const [pickYear, setPickYear] = useState(now.getFullYear());
  const [rangeStart, setRangeStart] = useState(sel?.dk === 'range' ? sel.start : '');
  const [rangeEnd, setRangeEnd] = useState(sel?.dk === 'range' ? (sel._end || '') : '');

  const periods = sel?.dk === 'periods' ? sel.periods : [];
  const activePreset = sel?.dk === 'relative' ? sel.preset : null;
  const periodTokens = new Set(periods.map(p => p.token));

  // Preset memory: offer each user their most-used relative preset first.
  const { user } = useAuth();
  const [counts, setCounts] = useState(() => (user?.preferences?.date_preset_counts) || {});
  const presetKeys = new Set(RELATIVE_PRESETS.map(p => p.key));
  const favoritePreset = Object.entries(counts)
    .filter(([k, v]) => presetKeys.has(k) && v > 0)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  const favoriteDef = favoritePreset ? RELATIVE_PRESETS.find(p => p.key === favoritePreset) : null;

  const applyPreset = (key) => {
    if (activePreset === key) { onChange(null); return; }
    onChange({ dk: 'relative', preset: key });
    setCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
    api.trackDatePreset(key).catch(() => {});
  };

  const years = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 7; y--) years.push(y);

  const emitPeriods = (list) => {
    if (!list.length) { onChange(null); return; }
    onChange({ dk: 'periods', grain, periods: list });
  };

  const togglePeriod = (n) => {
    // For the year grid, the clicked value IS the year; month/quarter use the
    // separate pickYear scope. Without this, every year button toggled pickYear
    // (the current year) so only one year could ever be selected.
    const yr = grain === 'year' ? n : pickYear;
    const p = makePeriod(grain, yr, n, fsm);
    const exists = periodTokens.has(p.token);
    const currentSameGrain = sel?.dk === 'periods' && sel.grain === grain ? periods : [];
    const next = exists ? currentSameGrain.filter(x => x.token !== p.token) : [...currentSameGrain, p];
    emitPeriods(next);
  };

  const applyRange = (s, e) => {
    setRangeStart(s); setRangeEnd(e);
    if (s && e) onChange({ dk: 'range', start: s, _end: e, endExcl: rangeEndExcl(e) });
    else if (!s && !e) onChange(null);
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Compact summary shown on the collapsed trigger.
  let summaryText = 'All dates';
  let hasSelection = false;
  if (activePreset) {
    const def = RELATIVE_PRESETS.find(p => p.key === activePreset);
    summaryText = def ? (isFiscal && def.fiscalLabel ? def.fiscalLabel : def.label) : activePreset;
    hasSelection = true;
  } else if (periods.length) {
    summaryText = periods.length <= 2 ? periods.map(p => p.label).join(', ') : `${periods.length} periods`;
    hasSelection = true;
  } else if (sel?.dk === 'range' && (sel.start || sel._end)) {
    summaryText = `${sel.start || '…'} → ${sel._end || '…'}`;
    hasSelection = true;
  }

  const body = (
    <div className="p-2.5">
      {/* Granularity switch */}
      <div className="flex gap-0.5 mb-1.5 bg-muted/20 rounded-lg p-0.5" data-testid={`date-grain-${dimension}`}>
        {GRAINS.map(g => (
          <button key={g.key} onClick={() => setGrain(g.key)}
            data-testid={`date-grain-${dimension}-${g.key}`}
            className={'flex-1 text-[9px] py-1 rounded-md transition-colors ' + (grain === g.key ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:bg-muted/40')}>
            {g.label}
          </button>
        ))}
      </div>

      {/* Most-used preset (offered first) */}
      {favoriteDef && (
        <button onClick={() => applyPreset(favoriteDef.key)}
          data-testid={`date-favorite-preset-${dimension}`}
          title="Your most-used date range"
          className={'w-full flex items-center justify-center gap-1 text-[9px] px-2 py-1 mb-1.5 rounded-lg border transition-colors ' + (activePreset === favoriteDef.key ? 'bg-primary/20 text-primary border-primary/40 font-semibold' : 'bg-amber-400/10 text-amber-600 dark:text-amber-400 border-amber-400/30 hover:bg-amber-400/20')}>
          <Star className="w-2.5 h-2.5 fill-current" />
          Most used: {isFiscal && favoriteDef.fiscalLabel ? favoriteDef.fiscalLabel : favoriteDef.label}
        </button>
      )}

      {/* Relative presets */}
      <div className="flex flex-wrap gap-1 mb-1.5">
        {RELATIVE_PRESETS.map(p => (
          <button key={p.key} onClick={() => applyPreset(p.key)}
            data-testid={`date-preset-${dimension}-${p.key}`}
            className={'text-[8px] px-1.5 py-0.5 rounded-full border transition-colors ' + (activePreset === p.key ? 'bg-primary/20 text-primary border-primary/40 font-semibold' : 'border-border/30 text-muted-foreground hover:text-primary hover:border-primary/30')}>
            {isFiscal && p.fiscalLabel ? p.fiscalLabel : p.label}
          </button>
        ))}
      </div>

      {/* Range picker */}
      {grain === 'range' && (
        <div className="flex items-center gap-1">
          <Input type="date" value={rangeStart} onChange={e => applyRange(e.target.value, rangeEnd)}
            data-testid={`date-range-start-${dimension}`} className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 flex-1" />
          <span className="text-[8px] text-muted-foreground">to</span>
          <Input type="date" value={rangeEnd} onChange={e => applyRange(rangeStart, e.target.value)}
            data-testid={`date-range-end-${dimension}`} className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 flex-1" />
        </div>
      )}

      {/* Year scope selector for month/quarter */}
      {(grain === 'month' || grain === 'quarter') && (
        <select value={pickYear} onChange={e => setPickYear(Number(e.target.value))}
          data-testid={`date-pickyear-${dimension}`}
          className="w-full mb-1.5 text-[10px] rounded-lg bg-muted/30 border border-border/30 px-2 py-1 text-foreground">
          {years.map(y => <option key={y} value={y}>{isFiscal ? `FY${y}` : y}</option>)}
        </select>
      )}

      {/* Month grid */}
      {grain === 'month' && (
        <div className="grid grid-cols-4 gap-1">
          {monthNames.map((m, i) => {
            const on = periodTokens.has(`${pickYear}-${String(i + 1).padStart(2, '0')}`);
            return <button key={m} onClick={() => togglePeriod(i + 1)}
              data-testid={`date-month-${dimension}-${i + 1}`}
              className={'text-[9px] py-1 rounded-md transition-colors ' + (on ? 'bg-primary text-primary-foreground font-semibold' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50')}>{m}</button>;
          })}
        </div>
      )}

      {/* Quarter grid */}
      {grain === 'quarter' && (
        <div className="grid grid-cols-4 gap-1">
          {[1, 2, 3, 4].map(q => {
            const on = periodTokens.has(`${pickYear}-Q${q}`);
            return <button key={q} onClick={() => togglePeriod(q)}
              data-testid={`date-quarter-${dimension}-${q}`}
              className={'text-[9px] py-1 rounded-md transition-colors ' + (on ? 'bg-primary text-primary-foreground font-semibold' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50')}>Q{q}</button>;
          })}
        </div>
      )}

      {/* Year grid */}
      {grain === 'year' && (
        <div className="grid grid-cols-3 gap-1">
          {years.map(y => {
            const on = periodTokens.has(`${y}`);
            return <button key={y} onClick={() => togglePeriod(y)}
              data-testid={`date-year-${dimension}-${y}`}
              className={'text-[9px] py-1 rounded-md transition-colors ' + (on ? 'bg-primary text-primary-foreground font-semibold' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50')}>{isFiscal ? `FY${y}` : y}</button>;
          })}
        </div>
      )}

      {/* Selected period chips */}
      {periods.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1.5 items-center">
          {periods.map(p => (
            <Badge key={p.token} className="text-[8px] bg-primary/15 text-primary border-primary/20 gap-0.5 cursor-pointer"
              onClick={() => emitPeriods(periods.filter(x => x.token !== p.token))}>{p.label} <X className="w-2 h-2" /></Badge>
          ))}
          <button onClick={() => onChange(null)} data-testid={`date-clear-${dimension}`}
            className="text-[8px] px-1.5 py-0.5 rounded-full text-muted-foreground hover:text-destructive">Clear all</button>
        </div>
      )}
      {sel?.dk === 'range' && (rangeStart || rangeEnd) && (
        <button onClick={() => applyRange('', '')} data-testid={`date-clear-${dimension}`}
          className="text-[8px] mt-1.5 px-1.5 py-0.5 rounded-full text-muted-foreground hover:text-destructive">Clear</button>
      )}
      {activePreset && (
        <button onClick={() => onChange(null)} data-testid={`date-clear-${dimension}`}
          className="text-[8px] mt-1.5 px-1.5 py-0.5 rounded-full text-muted-foreground hover:text-destructive">Clear</button>
      )}
    </div>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`slicer-${dimension}`}
          className="glass-card-light rounded-xl px-2.5 py-1.5 min-w-[160px] max-w-[240px] flex items-center gap-1.5 text-left hover:border-primary/40 transition-colors">
          <CalendarDays className="w-3 h-3 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[8px] text-muted-foreground uppercase tracking-wider truncate">{label}</p>
            <p className={'text-[10px] truncate ' + (hasSelection ? 'text-foreground font-medium' : 'text-muted-foreground')}
              data-testid={`date-summary-${dimension}`}>{summaryText}</p>
          </div>
          <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] p-0 glass-card border-border/50 rounded-xl overflow-hidden">
        <div className="flex items-center gap-1 px-2.5 pt-2.5">
          <CalendarDays className="w-3 h-3 text-muted-foreground" />
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</p>
        </div>
        {body}
      </PopoverContent>
    </Popover>
  );
}
